/* eslint-disable camelcase */
import { useQuery } from '@tanstack/react-query';

import queryKeys from '@pray/shared/queryKeys';
import api from '@pray/shared/utils/api';
import storage from '@pray/shared/utils/storage';

import type { EmotionalJourneyData, EmotionalJourneyPoint, Emotion } from './types';

// API response types (snake_case from backend)
type ApiEmotionalJourneyPoint = {
  message_index: number;
  sentiment: number;
  emotion: Emotion;
  intensity: number;
  created_at: string;
};

type ApiEmotionalJourneyData = {
  journey: ApiEmotionalJourneyPoint[];
  summary: {
    starting_sentiment: number;
    ending_sentiment: number;
    trajectory: 'improving' | 'declining' | 'stable';
    message_count: number;
  };
};

type ApiEmotionalJourneyResponse = {
  data: ApiEmotionalJourneyData;
};

// Transform snake_case API response to camelCase for frontend
function transformResponse(apiData: ApiEmotionalJourneyData): EmotionalJourneyData {
  return {
    journey: apiData.journey.map(
      (point): EmotionalJourneyPoint => ({
        messageIndex: point.message_index,
        sentiment: point.sentiment,
        emotion: point.emotion,
        intensity: point.intensity,
      })
    ),
    summary: {
      startingSentiment: apiData.summary.starting_sentiment,
      endingSentiment: apiData.summary.ending_sentiment,
      trajectory: apiData.summary.trajectory,
    },
  };
}

export async function getConversationJourney(artistId: string, conversationId: string): Promise<EmotionalJourneyData> {
  if (!artistId) throw Error('Artist ID is required');
  if (!conversationId) throw Error('Conversation ID is required');

  const url = `/web/studio/artists/${artistId}/conversations/${conversationId}/journey`;
  const response = await api.executeGetRequest<ApiEmotionalJourneyResponse>(url);

  return transformResponse(response.data.data);
}

export function useConversationJourney(artistId: string, conversationId: string | null) {
  const user = storage.getUser();

  return useQuery({
    enabled: !!artistId && !!conversationId && !!user?.is_registered,
    queryKey: queryKeys.artistConversationJourney(artistId, conversationId),
    queryFn: () => getConversationJourney(artistId, conversationId!),
    staleTime: 60_000, // Consider fresh for 1 minute
  });
}
